import { useContext, useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { useSearchParams } from 'react-router-dom';

import config from '../../../config';

import { ACCESS_TOKEN_KEY, USER_ROLE, APP_USER_ID } from '../../../constants';
import {
	AUTH_KEY,
	ACCESS_TOKEN_EXPIRATION_KEY,
	REFRESH_TOKEN_KEY,
	REFRESH_TOKEN_EXPIRATION_KEY,
	parseJwt,
	FEATURES_KEY,
	IS_SUBSCRIBED_KEY,
} from '../../../views/home/auth/Authentication.hooks';
import { AppContext } from '../../../App.context';

export function useCallback(): CallbackResponse {
	const [searchParams] = useSearchParams();
	const { rebuildApolloClient, kv } = useContext(AppContext);
	const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<Error | null>(null);
	const code = searchParams.get('code');

	useEffect(() => {
		if (!code) {
			setError(new Error('Missing code'));
			setIsLoading(false);
			return;
		}

		const redirecUrl = [
			location.protocol,
			'//',
			location.host,
			location.pathname,
		].join('');

		const params = new URLSearchParams({
			code: `${code}`,
			redirect_uri: redirecUrl,
		});
		const url = [
			config.build.host,
			'/auth/authorize',
			'?',
			params.toString(),
		].join('');
		fetch(url, {
			method: 'GET',
			headers: {
				'app-key': config.build.appKey,
				'Content-Type': 'application/json',
			},
		})
			.then((r) => r.json())
			.then((r) => {
				saveTokens(r);
				kv.clear();

				if (localStorage.getItem(IS_SUBSCRIBED_KEY) === 'true') {
					setIsSubscribed(true);
				}
				rebuildApolloClient();
				setIsLoading(false);
			})
			.catch((e) => {
				setError(e);
				setIsLoading(false);
			});
	}, [code]);

	return {
		isLoading,
		error,
		isSubscribed,
	};
}

export function saveTokens(content: any) {
	const { access_token, refresh_token, expires } = content;

	// JWT Parse Data
	const accessTokenData = parseJwt(access_token);
	const refreshTokenData = parseJwt(refresh_token);

	// Calculate expiration dates
	const accessExpirationDate = DateTime.now()
		.plus({ seconds: expires })
		.toISO();
	const refreshExpirationDate = DateTime.fromSeconds(
		refreshTokenData.exp,
	).toISO();

	clearTokenData();

	// Store in localStorage
	localStorage.setItem(AUTH_KEY, 'true');
	localStorage.setItem(USER_ROLE, 'user');
	if (accessTokenData.sub) {
		localStorage.setItem(APP_USER_ID, accessTokenData.sub);
	} else {
		if (localStorage.getItem(APP_USER_ID)) {
			localStorage.removeItem(APP_USER_ID);
		}
	}

	localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
	localStorage.setItem(ACCESS_TOKEN_EXPIRATION_KEY, accessExpirationDate);

	localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
	localStorage.setItem(REFRESH_TOKEN_EXPIRATION_KEY, refreshExpirationDate);

	if (accessTokenData.entitlements) {
		if (accessTokenData.entitlements?.subscription) {
			localStorage.setItem(IS_SUBSCRIBED_KEY, 'true');
		}
		if (accessTokenData.entitlements.features) {
			localStorage.setItem(
				FEATURES_KEY,
				JSON.stringify(accessTokenData.entitlements.features),
			);
		}
	}
}

function clearTokenData() {
	const keys = [
		AUTH_KEY,
		USER_ROLE,
		ACCESS_TOKEN_KEY,
		ACCESS_TOKEN_EXPIRATION_KEY,
		REFRESH_TOKEN_KEY,
		REFRESH_TOKEN_EXPIRATION_KEY,
		IS_SUBSCRIBED_KEY,
		FEATURES_KEY,
	];

	for (const key of keys) {
		if (localStorage.getItem(key)) {
			localStorage.removeItem(key);
		}
	}
}

export interface CallbackResponse {
	isLoading: boolean;
	error: Error | null;
	isSubscribed?: boolean;
}
